import axios from 'axios'
import * as React from 'react'

export interface AuthContext {
    isAuthenticated: boolean
    login: (username: string, password: string) => Promise<void>
    logout: () => Promise<void>
    authResult: AuthResult | null
    refreshToken: () => Promise<boolean>
}

const AuthContext = React.createContext<AuthContext | null>(null)

const AUTH_KEY = 'coolfocus.auth.result'
const REFRESH_TOKEN_KEY = 'coolfocus.refresh.token'

// Add these constants at the top with other constants
const REFRESH_RETRY_ATTEMPTS = 3
const REFRESH_RETRY_DELAY_MS = 1000
const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000 // 1 minute
const CLOCK_SKEW_BUFFER_MS = 5 * 60 * 1000 // 5 minutes

function getStoredAuthResult(): AuthResult | null {
    const stored = localStorage.getItem(AUTH_KEY)
    if (!stored) {
        return null
    }
    try {
        return JSON.parse(stored)
    } catch {
        return null
    }
}

function setStoredAuthResult(authResult: AuthResult | null) {
    if (authResult) {
        localStorage.setItem(AUTH_KEY, JSON.stringify(authResult))
    } else {
        localStorage.removeItem(AUTH_KEY)
    }
}

function getStoredRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
}

function setStoredRefreshToken(refreshToken: string | null) {
    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY)
    }
}

export interface AuthResult {
    mfaSent?: boolean
    id: string
    accessToken?: string
    token?: string // Legacy support
    refreshToken?: string
    accessTokenExpiresAt?: string
    refreshTokenExpiresAt?: string
    tokenType?: string
    firstName: string
    lastName: string
    emailAddress: string
    customerName: string
    userHash: string
    cF4Access: true
    isAdmin: boolean
    customerId: string
}

export interface AuthResponse {
    id: string
    firstName: string
    lastName: string
    emailAddress: string
    customerName: string
    customerId: string
    userHash: string
    cF4Access: boolean
    cF4CPCAccess: boolean
    isAdmin: boolean
    accessToken: string
    refreshToken: string
    accessTokenExpiresAt: string
    refreshTokenExpiresAt: string
    tokenType: string
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [authResult, setAuthResult] = React.useState<AuthResult | null>(null)
    const isAuthenticated = !!authResult

    // Initialize auth state from storage
    React.useEffect(() => {
        const storedAuth = getStoredAuthResult()
        setAuthResult(storedAuth)
    }, [])

    // Ref to store the current refresh promise to prevent concurrent refresh attempts
    const refreshPromiseRef = React.useRef<Promise<boolean> | null>(null)

    const logout = React.useCallback(async () => {
        try {
            const refreshToken = getStoredRefreshToken()

            // Revoke the refresh token if it exists
            if (refreshToken) {
                try {
                    await axios.post(`${import.meta.env.VITE_API_URL}/auth/revoke`, {
                        refreshToken
                    })
                } catch {
                    // Ignore errors during logout
                }
            }

            // First clear the auth result state
            setAuthResult(null)

            // Then clear storage in the next tick to ensure React state updates first
            setTimeout(() => {
                localStorage.clear()
                setStoredAuthResult(null)
                setStoredRefreshToken(null)
            }, 0)

            // Clear any pending refresh promise
            refreshPromiseRef.current = null
        } catch (error) {
            console.error('Error during logout:', error)
            // Even if there's an error, ensure we clear the state
            setAuthResult(null)
            localStorage.clear()
            setStoredAuthResult(null)
            setStoredRefreshToken(null)
        }
    }, [])

    const refreshToken = React.useCallback(async (): Promise<boolean> => {
        // If there's already a refresh in progress, return that promise
        if (refreshPromiseRef.current) {
            return refreshPromiseRef.current
        }

        // Create a new refresh promise
        const refreshOperation = async (): Promise<boolean> => {
            const storedRefreshToken = getStoredRefreshToken()

            if (!storedRefreshToken) {
                return false
            }

            try {
                const response = await axios.post<AuthResponse>(
                    `${import.meta.env.VITE_API_URL}/auth/refresh`,
                    { refreshToken: storedRefreshToken }
                )

                if (response.data) {
                    const newAuthResult: AuthResult = {
                        id: response.data.id,
                        accessToken: response.data.accessToken,
                        token: response.data.accessToken, // Legacy support
                        refreshToken: response.data.refreshToken,
                        accessTokenExpiresAt: response.data.accessTokenExpiresAt,
                        refreshTokenExpiresAt: response.data.refreshTokenExpiresAt,
                        tokenType: response.data.tokenType,
                        firstName: response.data.firstName,
                        lastName: response.data.lastName,
                        emailAddress: response.data.emailAddress,
                        customerName: response.data.customerName,
                        userHash: response.data.userHash,
                        cF4Access: true,
                        isAdmin: response.data.isAdmin,
                        customerId: response.data.customerId,
                    }

                    setStoredAuthResult(newAuthResult)
                    setStoredRefreshToken(response.data.refreshToken)
                    setAuthResult(newAuthResult)
                    return true
                }
            } catch (error) {
                // Refresh failed, logout user
                await logout()
                return false
            } finally {
                // Clear the promise reference when done
                refreshPromiseRef.current = null
            }

            return false
        }

        // Store the promise and return it
        refreshPromiseRef.current = refreshOperation()
        return refreshPromiseRef.current
    }, [logout])

    const login = React.useCallback(async (username: string, password: string) => {
        const loginResult = await axios.post<AuthResponse | { mfaSent: boolean, verificationSID: string, id: string }>(
            `${import.meta.env.VITE_API_URL}/auth/authenticate`,
            {
                username,
                password
            }
        )

        if (!loginResult.data) {
            throw new Error('Login failed')
        }

        // Check if it's an MFA response
        if ('mfaSent' in loginResult.data && loginResult.data.mfaSent) {
            throw new Error('MFA is required')
        }

        // It's a successful authentication response
        const authResponse = loginResult.data as AuthResponse
        const newAuthResult: AuthResult = {
            id: authResponse.id,
            accessToken: authResponse.accessToken,
            token: authResponse.accessToken, // Legacy support
            refreshToken: authResponse.refreshToken,
            accessTokenExpiresAt: authResponse.accessTokenExpiresAt,
            refreshTokenExpiresAt: authResponse.refreshTokenExpiresAt,
            tokenType: authResponse.tokenType,
            firstName: authResponse.firstName,
            lastName: authResponse.lastName,
            emailAddress: authResponse.emailAddress,
            customerName: authResponse.customerName,
            userHash: authResponse.userHash,
            cF4Access: true,
            isAdmin: authResponse.isAdmin,
            customerId: authResponse.customerId,
        }

        setStoredAuthResult(newAuthResult)
        setStoredRefreshToken(authResponse.refreshToken)
        setAuthResult(newAuthResult)
    }, [])

    return (
        <AuthContext.Provider value={{ isAuthenticated, authResult, login, logout, refreshToken }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = React.useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export function getCurrentUser(): Omit<AuthResult, 'token'> | null {
    const authResult = getStoredAuthResult()
    if (!authResult) {
        return null
    }
    const { token, ...userInfo } = authResult
    return userInfo
}

export function getToken(): string | null {
    const authResult = getStoredAuthResult()
    if (!authResult) {
        return null
    }
    // Support both new accessToken and legacy token field
    return authResult.accessToken || authResult.token || null
}

// Replace the global refresh promise with a more robust implementation
let globalRefreshPromise: Promise<boolean> | null = null
let globalRefreshPromiseResolve: ((value: boolean) => void) | null = null

async function startGlobalRefresh(refreshTokenValue: string): Promise<boolean> {
    // If there's already a refresh in progress, return that promise
    if (globalRefreshPromise) {
        return globalRefreshPromise
    }

    // Create a new promise that will be resolved by the refresh operation
    globalRefreshPromise = new Promise<boolean>((resolve) => {
        globalRefreshPromiseResolve = resolve
    })

    try {
        let attempts = 0
        let lastError: Error | null = null

        while (attempts < REFRESH_RETRY_ATTEMPTS) {
            try {
                const result = await refreshTokenSilently(refreshTokenValue)
                globalRefreshPromiseResolve?.(result)
                return result
            } catch (error) {
                lastError = error as Error
                attempts++
                if (attempts < REFRESH_RETRY_ATTEMPTS) {
                    await new Promise(resolve => setTimeout(resolve, REFRESH_RETRY_DELAY_MS))
                }
            }
        }

        // All retry attempts failed
        throw lastError || new Error('Refresh token failed after all retry attempts')
    } finally {
        // Clear the promise and resolver
        globalRefreshPromise = null
        globalRefreshPromiseResolve = null
    }
}

export async function getTokenAsync(): Promise<string | null> {
    const authResult = getStoredAuthResult()
    if (!authResult) {
        return null
    }

    const currentToken = authResult.accessToken || authResult.token || null
    if (!currentToken) {
        return null
    }

    // Check if token is expired or will expire soon
    if (authResult.accessTokenExpiresAt) {
        try {
            const expiresAt = new Date(authResult.accessTokenExpiresAt)
            const now = new Date()
            const bufferTime = TOKEN_EXPIRY_BUFFER_MS + CLOCK_SKEW_BUFFER_MS
            const expiryWithBuffer = new Date(now.getTime() + bufferTime)

            if (expiresAt <= expiryWithBuffer) {
                // Also check refresh token expiration
                if (authResult.refreshTokenExpiresAt) {
                    const refreshExpiresAt = new Date(authResult.refreshTokenExpiresAt)
                    if (refreshExpiresAt <= now) {
                        // Refresh token is expired, clear auth and return null
                        clearAuthStorage()
                        return null
                    }
                }

                const refreshTokenValue = getStoredRefreshToken()
                if (!refreshTokenValue) {
                    return null
                }

                try {
                    await startGlobalRefresh(refreshTokenValue)
                    const newAuthResult = getStoredAuthResult()
                    return newAuthResult?.accessToken || newAuthResult?.token || null
                } catch (error) {
                    console.error('Token refresh failed:', error)
                    clearAuthStorage()
                    return null
                }
            }
        } catch (error) {
            console.error('Error checking token expiration:', error)
            // If we can't parse the expiration date, assume token is invalid
            clearAuthStorage()
            return null
        }
    }

    return currentToken
}

// Helper function to clear only auth-related storage
function clearAuthStorage() {
    setStoredAuthResult(null)
    setStoredRefreshToken(null)
}

// Update refreshTokenSilently to use the new clearAuthStorage function
async function refreshTokenSilently(refreshTokenValue: string): Promise<boolean> {
    try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken: refreshTokenValue })
        })

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        if (data) {
            const newAuthResult: AuthResult = {
                id: data.id,
                accessToken: data.accessToken,
                token: data.accessToken, // Legacy support
                refreshToken: data.refreshToken,
                accessTokenExpiresAt: data.accessTokenExpiresAt,
                refreshTokenExpiresAt: data.refreshTokenExpiresAt,
                tokenType: data.tokenType,
                firstName: data.firstName,
                lastName: data.lastName,
                emailAddress: data.emailAddress,
                customerName: data.customerName,
                userHash: data.userHash,
                cF4Access: true,
                isAdmin: data.isAdmin,
                customerId: data.customerId,
            }

            setStoredAuthResult(newAuthResult)
            setStoredRefreshToken(data.refreshToken)
            return true
        }
    } catch (error) {
        clearAuthStorage()
        throw error
    }

    return false
}
